import {useCallback} from 'react';

import {Amplify} from 'aws-amplify';
import {
  autoSignIn as amplifyAutoSignIn,
  getCurrentUser as amplifyCurrentUser,
  AuthUser,
  confirmResetPassword,
  ConfirmResetPasswordInput,
  confirmSignIn,
  ConfirmSignInOutput,
  confirmSignUp,
  ConfirmSignUpInput,
  ConfirmSignUpOutput,
  fetchAuthSession,
  resendSignUpCode,
  resetPassword,
  ResetPasswordInput,
  ResetPasswordOutput,
  signIn,
  SignInInput,
  SignInOutput,
  signOut,
  signUp,
  SignUpInput,
  SignUpOutput,
  updateMFAPreference,
  updatePassword,
  UpdatePasswordInput,
} from 'aws-amplify/auth';

import {PASSWORD_EVENTS, RolesAccount} from '@/app/auth';
import {VERIFY_EVENTS} from '@/app/auth/events/VerifyEvents';
import {persistor, unSetUser, useAppDispatch} from '@/app/store';
import {setIsDesktop, setIsMobile} from '@/app/store/responsiveSlice';
import {unSetTokenData} from '@/app/store/tokenSlice';
import {sleep} from '@/app/utils/sleep';
import {PROJECT_TAGS} from '@/constants';
import {ENV} from '@/constants/EnvConstants';
import {HttpMethod} from '@/constants/httpMethod';
import {buildAmplifyClientMetadata} from '@/dataSource/utils/buildAmplifyClientMetadata';
import {useTrackingManager} from '@/hooks';
import {isProduction} from '@/utils/environment';
import {logBreadcrumb} from '@/utils/errors/logError';

import {
  CAwsAmplifyErrorTitles,
  CGetTokenConfig,
  PREFERRED_CHALLENGE,
} from './AmplifyConstants';
import {
  ClientMetadata,
  GetTokenInput,
  GetTokenPayload,
  ChangePasswordInput as IChangePasswordInput,
  ConfirmSignUpInput as IConfirmSignUpInput,
  ForgotPasswordConfirmInput as IForgotPasswordConfirmInput,
  ForgotPasswordInput as IForgotPasswordInput,
  IGetTokenResponse,
  SignInInput as ISignInInput,
  SignUpInput as ISignUpInput,
  IUserError,
} from './AmplifyInterfaces';
import {AuthFlowType, MFAPreferenceType} from './AmplifyTypes';
import {awsAmplifyConfiguration} from './aws-exports';

Amplify.configure(awsAmplifyConfiguration);

export const AmplifyService = () => {
  const {trackEvent, resetAnalytics} = useTrackingManager();
  const dispatch = useAppDispatch();

  const transformError = useCallback(
    async <T>(promise: Promise<T>, methodName: string, user?: IUserError) => {
      try {
        const result = await promise;
        return result;
      } catch (error: any) {
        const title = `${CAwsAmplifyErrorTitles.BASE}-${methodName}`;
        logBreadcrumb({
          category: title,
          type: 'query',
          message: `Error in ${methodName}`,
          level: 'warning',
          data: {
            ...(user?.phone && {'user.phone': user?.phone}),
            ...(user?.email && {'user.email': user?.email}),
            ...(user?.id && {'user.id': user?.id}),
            ...(error?.code && {'error.code': error?.code}),
            ...(error?.name && {'error.name': error?.name}),
            ...(error?.message && {'error.message': error?.message}),
            originalError: error,
          },
        });
        throw error;
      }
    },
    [],
  );

  const handleSignIn = useCallback(
    async (
      input: ISignInInput,
      authFlowType: AuthFlowType,
      clientMetadata?: ClientMetadata,
    ): Promise<SignInOutput> => {
      const params: SignInInput = {
        username: input.username,
        password: input.password,
        options: {
          authFlowType,
          clientMetadata,
          preferredChallenge: PREFERRED_CHALLENGE.SMS,
        },
      };
      return transformError(
        signIn(params),
        CAwsAmplifyErrorTitles.HANDLE_SIGN_IN,
        {
          phone: input.username,
        },
      );
    },
    [transformError],
  );

  const handleSignInConfirmation = useCallback(
    async (SMSCode: string, username: string): Promise<ConfirmSignInOutput> => {
      return transformError(
        confirmSignIn({challengeResponse: SMSCode}),
        CAwsAmplifyErrorTitles.HANDLE_SIGN_IN_CONFIRM,
        {phone: username},
      );
    },
    [transformError],
  );

  const handleSignUp = useCallback(
    async (input: ISignUpInput): Promise<SignUpOutput> => {
      const params: SignUpInput = {
        username: input.username,
        password: input.password,
        options: {
          userAttributes: {
            name: input.name,
            email: input.email,
            phone_number: input.phoneNumber, // E.164 number convention
          },
          clientMetadata: {
            shared_key: input.shared_key,
          },
        },
      };

      return transformError(
        signUp(params),
        CAwsAmplifyErrorTitles.HANDLE_SIGN_UP,
        {
          phone: input.username,
          email: input.email,
        },
      );
    },
    [transformError],
  );

  const handleSignUpConfirmation = useCallback(
    async (input: IConfirmSignUpInput): Promise<ConfirmSignUpOutput> => {
      const params: ConfirmSignUpInput = {
        confirmationCode: input.confirmationCode,
        username: input.username,
        options: {
          clientMetadata: {
            first_name: input.first_name,
            last_name: input.last_name,
            referral_code: input?.referral_code,
            invitation_code: input?.invitation_code,
            role: input?.role ?? RolesAccount.PARENT,
            shared_key: input?.shared_key || '',
            marketing_opt_in: String(input?.marketing_opt_in ?? false),
            ad_ids: buildAmplifyClientMetadata(),
            ...(input.enableBankTesting !== undefined && {
              enable_bank_testing: String(input.enableBankTesting),
            }),
          },
        },
      };

      return transformError(
        confirmSignUp(params),
        CAwsAmplifyErrorTitles.HANDLE_SIGN_UP_CONFIRM,
        {phone: input.username},
      );
    },
    [transformError],
  );

  const autoSignIn = useCallback(async (): Promise<SignInOutput> => {
    return transformError(
      amplifyAutoSignIn(),
      CAwsAmplifyErrorTitles.AUTO_SIGN_IN,
    );
  }, [transformError]);

  const handleResendCodeSignUpConfirmation = useCallback(
    async (username: string): Promise<void> => {
      return transformError(
        resendSignUpCode({username}),
        CAwsAmplifyErrorTitles.HANDLE_RESEND_CODE_SIGN_UP_CONFIRM,
        {phone: username},
      ).then(() => {});
    },
    [transformError],
  );

  const handleChangePassword = useCallback(
    async (input: IChangePasswordInput) => {
      const params: UpdatePasswordInput = {
        newPassword: input.newPassword,
        oldPassword: input.oldPassword,
      };

      return transformError(
        updatePassword(params),
        CAwsAmplifyErrorTitles.HANDLE_CHANGE_PASSWORD,
      );
    },
    [transformError],
  );

  const handleSendRecoveryEmail = useCallback(
    async (
      input: IForgotPasswordInput,
      clientMetadata?: ClientMetadata,
    ): Promise<ResetPasswordOutput> => {
      const params: ResetPasswordInput = {
        username: input.username,
        options: {
          clientMetadata,
        },
      };
      return transformError(
        resetPassword(params),
        CAwsAmplifyErrorTitles.HANDLE_FORGOT_PASSWORD,
        {phone: input.username},
      );
    },
    [transformError],
  );

  const handleConfirmForgotPassword = useCallback(
    async (input: IForgotPasswordConfirmInput): Promise<void> => {
      const params: ConfirmResetPasswordInput = {
        confirmationCode: input.confirmationCode,
        newPassword: input.password,
        username: input.username,
      };

      try {
        await transformError(
          confirmResetPassword(params),
          CAwsAmplifyErrorTitles.HANDLE_CONFIRM_FORGOT_PASSWORD,
        );

        trackEvent(PASSWORD_EVENTS.API_RESET_PASSWORD_SUCCESS, {
          project_tag: PROJECT_TAGS.PASSWORD,
        });
      } catch (err: any) {
        trackEvent(PASSWORD_EVENTS.API_RESET_PASSWORD_ERROR, {
          project_tag: PROJECT_TAGS.PASSWORD,
          error: {
            code: err?.code || err?.name,
            message: err?.message,
          },
          metadata: {
            email: input.username,
            code: input.confirmationCode,
          },
        });
        throw err;
      }
    },
    [trackEvent, transformError],
  );

  const updateMFA = useCallback(
    async (sms: MFAPreferenceType): Promise<void> => {
      return transformError(
        updateMFAPreference({
          sms,
        }),
        CAwsAmplifyErrorTitles.UPDATE_MFA,
      );
    },
    [transformError],
  );

  const clearData = useCallback((): void => {
    dispatch(unSetUser(null));
    dispatch(unSetTokenData());
    dispatch(window.innerWidth < 960 ? setIsMobile() : setIsDesktop());
    localStorage.clear();
    sessionStorage.clear();
    resetAnalytics();
    signOut();
    persistor.purge();
  }, [dispatch, resetAnalytics]);

  const handleSignOut = useCallback(async (): Promise<void> => {
    return transformError(
      signOut(),
      CAwsAmplifyErrorTitles.HANDLE_SIGN_OUT,
    ).then(() => {
      clearData();
    });
  }, [clearData, transformError]);

  const getCurrentSession = useCallback(
    async (forceRefresh = false): Promise<string> => {
      return transformError(
        fetchAuthSession({forceRefresh}).then(
          auth => auth?.tokens?.idToken.toString() || '',
        ),
        CAwsAmplifyErrorTitles.GET_CURRENT_SESSION,
      );
    },
    [transformError],
  );

  const getCurrentUser = useCallback(async (): Promise<AuthUser> => {
    return transformError(
      amplifyCurrentUser(),
      CAwsAmplifyErrorTitles.GET_CURRENT_USER,
    );
  }, [transformError]);

  const hasSession = useCallback(async (): Promise<boolean> => {
    try {
      const userAuth = await getCurrentUser();
      return Boolean(userAuth?.userId);
    } catch {
      return false;
    }
  }, [getCurrentUser]);

  const doFetchToken = useCallback(
    async (input: GetTokenInput): Promise<IGetTokenResponse> => {
      const body: GetTokenPayload = {
        target: input.target,
        shared_key: input.shared_key,
      };

      let responseStatus = '';

      try {
        const response = await fetch(
          `${ENV.VITE_APIS_USERS_DOMAIN_URL}/auth/get_token`,
          {
            method: HttpMethod.POST,
            headers: {
              'Content-Type': 'application/json',
              ...(!isProduction && {'x-modak-skip-waf': ENV.VITE_WAF_SKIP_KEY}),
            },
            body: JSON.stringify(body),
          },
        );

        responseStatus = `HTTP ${response.status}: ${response.statusText}`;

        const data = await response.json();

        if (!data?.code) {
          const dataStringJson = JSON.stringify(data);
          const codeError = responseStatus;
          trackEvent(VERIFY_EVENTS.API_GET_TOKEN_NO_CODE, {
            project_tag: PROJECT_TAGS.ACCOUNT_ACCESS,
            error: {
              code: codeError,
              message: `Unexpected response format: ${dataStringJson}`,
            },
            metadata: {
              shared_key: body.shared_key,
            },
          });
          return {
            code: '',
            status: response.status,
            error: dataStringJson || 'Invalid response format',
          };
        }

        return {
          code: data.code,
          status: response.status,
        };
      } catch (error) {
        const instanceError =
          error instanceof Error ? error : new Error('Unknown error');
        trackEvent(VERIFY_EVENTS.API_GET_TOKEN_CATCH_ERROR, {
          project_tag: PROJECT_TAGS.ACCOUNT_ACCESS,
          error: {
            code: instanceError.name,
            message: `${responseStatus} - ${instanceError.message}`,
          },
          metadata: {
            shared_key: body.shared_key,
          },
        });
        return {
          code: '',
          status: 0,
          error: `${responseStatus} - ${instanceError.message}`,
        };
      }
    },
    [trackEvent],
  );

  const getToken = useCallback(
    async (input: GetTokenInput): Promise<string> => {
      for (let attempt = 1; attempt <= CGetTokenConfig.MAX_RETRIES; attempt++) {
        const result = await doFetchToken(input);
        if (result.code) {
          return result.code;
        }

        if (attempt < CGetTokenConfig.MAX_RETRIES) {
          await sleep(CGetTokenConfig.RETRY_DELAY_MS);
        }
      }

      return '';
    },
    [doFetchToken],
  );

  return {
    handleSignIn,
    handleSignInConfirmation,
    handleSignUp,
    handleSignUpConfirmation,
    autoSignIn,
    handleResendCodeSignUpConfirmation,
    handleChangePassword,
    handleSendRecoveryEmail,
    handleConfirmForgotPassword,
    updateMFA,
    handleSignOut,
    getCurrentSession,
    getCurrentUser,
    hasSession,
    getToken,
    clearData,
  };
};
